import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import DOMPurify from "dompurify";

const AppContext = createContext(null);

// For BR: any "*_br" field (when non-empty) fills the "*_pt" slot so every
// component that reads the PT field automatically shows the BR content. Falls
// back to PT when a BR value is not set. Recurses into nested objects/arrays.
export function localizeForBr(obj) {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(localizeForBr);
  const out = {};
  for (const k of Object.keys(obj)) out[k] = localizeForBr(obj[k]);
  for (const k of Object.keys(obj)) {
    if (k.endsWith("_br") && obj[k]) out[`${k.slice(0, -3)}_pt`] = obj[k];
  }
  return out;
}

export function AppProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("lang") || "pt");
  // True once the visitor has confirmed the final language on the entry splash.
  // Background music must stay silent until this is set. Persisted per session.
  const [langConfirmed, setLangConfirmed] = useState(
    () => sessionStorage.getItem("langConfirmed") === "1"
  );
  const confirmLang = useCallback((l) => {
    if (l) {
      setLang(l);
      localStorage.setItem("lang", l);
    }
    sessionStorage.setItem("langConfirmed", "1");
    setLangConfirmed(true);
  }, []);
  const [texts, setTexts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nm_texts_cache") || "null") || {}; } catch { return {}; }
  });
  const DEFAULT_SETTINGS = {
    snow_enabled: true,
    confetti_enabled: true,
    lights_enabled: true,
    ranking_top_n: 10,
    show_school_in_ranking: true,
  };
  const [settings, setSettings] = useState(() => {
    try {
      const c = JSON.parse(localStorage.getItem("nm_settings_cache") || "null");
      return c ? { ...DEFAULT_SETTINGS, ...c } : DEFAULT_SETTINGS;
    } catch { return DEFAULT_SETTINGS; }
  });
  const [admin, setAdmin] = useState(null); // null=loading, false=guest, obj=user
  // Instant render (no loading screen) whenever we already have cached content;
  // fresh data is refreshed silently in the background (stale-while-revalidate).
  const [loadedOnce, setLoadedOnce] = useState(
    () => !!localStorage.getItem("nm_texts_cache") && !!localStorage.getItem("nm_settings_cache")
  );
  // Global button style inherited from the Home buttons (X). Format/effect/font
  // are applied site-wide to action buttons; each button keeps its own color.
  const [buttonStyle, setButtonStyle] = useState({ format: "clay", effect: "lift", titleFont: "fredoka" });

  const refreshButtonStyle = useCallback(async () => {
    try {
      const { data } = await api.get("/side-buttons");
      const list = data || [];
      const src = list.find((b) => b.group === "alunos") || list[0];
      if (src) setButtonStyle({ format: src.format || "clay", effect: src.effect || "lift", titleFont: src.title_font || "fredoka" });
    } catch (e) { /* keep defaults */ }
  }, []);

  const refreshTexts = useCallback(async () => {
    try {
      const { data } = await api.get("/texts");
      setTexts(data || {});
      try { localStorage.setItem("nm_texts_cache", JSON.stringify(data || {})); } catch {}
    } catch (e) {
      console.error("texts fetch failed", e);
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings((prev) => ({ ...prev, ...data }));
      try { localStorage.setItem("nm_settings_cache", JSON.stringify(data || {})); } catch {}
    } catch (e) {
      console.error("settings fetch failed", e);
    }
  }, []);

  const checkAdmin = useCallback(async () => {
    // Only check if a token exists locally to avoid 401 console noise on public pages
    if (!localStorage.getItem("admin_token")) {
      setAdmin(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setAdmin(data);
    } catch {
      setAdmin(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([refreshTexts(), refreshSettings(), checkAdmin(), refreshButtonStyle()]);
      setLoadedOnce(true);
    })();
  }, [refreshTexts, refreshSettings, checkAdmin, refreshButtonStyle]);

  const switchLang = () => {
    const next = lang === "pt" ? "en" : "pt";
    setLang(next);
    localStorage.setItem("lang", next);
  };
  const chooseLang = (l) => {
    setLang(l);
    localStorage.setItem("lang", l);
  };

  const t = (key, fallback = "") => {
    const entry = texts[key];
    if (!entry) return fallback || key;
    // BR is strict (no PT fallback) per requirement: if the BR text is empty,
    // fall back to the code default / key, not to the European PT text.
    if (lang === "br") return entry.br || entry.pt || entry.en || fallback || key;
    return entry[lang] || entry.pt || entry.en || fallback || key;
  };

  // HTML-safe version of t(): sanitizes admin-authored formatting for display.
  const tHtml = (key, fallback = "") => DOMPurify.sanitize(t(key, fallback), {
    ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "s", "span", "font", "br", "p", "div",
      "ul", "ol", "li", "a", "h1", "h2", "h3", "h4", "small", "sup", "sub"],
    ALLOWED_ATTR: ["style", "href", "target", "rel", "color", "size", "class", "align"],
    FORBID_ATTR: ["onerror", "onload", "onclick"],
  });

  // Public/site-facing settings: for BR, "*_br" values fill their "*_pt" slot
  // so every component (which reads PT) shows BR content with PT fallback.
  // Admin editors must use `settingsRaw` to avoid saving BR values into PT.
  const publicSettings = lang === "br" ? localizeForBr(settings) : settings;

  return (
    <AppContext.Provider
      value={{
        lang,
        setLang,
        switchLang,
        chooseLang,
        langConfirmed,
        confirmLang,
        texts,
        refreshTexts,
        settings: publicSettings,
        settingsRaw: settings,
        refreshSettings,
        admin,
        setAdmin,
        checkAdmin,
        loadedOnce,
        buttonStyle,
        refreshButtonStyle,
        t,
        tHtml,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
